import type { ApolloClient } from "@apollo/client";
import type { BookingOutcome } from "../booking/operations";
import { parseCodeRequestAnswer, parseCodeVerifyAnswer, type Call, type CodeRequestAnswer, type CodeVerifyAnswer } from "./answers";
import type { FrozenGuestBookingInput } from "./checkout";
import { CREATE_GUEST_BOOKING, REQUEST_GUEST_EMAIL_CODE, VERIFY_GUEST_EMAIL_CODE } from "./operations";
import { guestBookingFailureFrom, type GuestCheckoutPort, type SubmitResult } from "./port";

/**
 * THE ONE ADAPTER: GuestCheckoutPort over the booking-v1 1.11 operations.
 *
 * Its whole job is translation, and it decides nothing:
 *
 * - Every method RESOLVES. A thrown transport, a GraphQL error on the code
 *   calls, or a missing payload is `lost` or `malformed`; the machine decides
 *   what that means. Only `submit` classifies a refusal, and it does so with
 *   `guestBookingFailureFrom`, so the transport gates (5xx, 408,
 *   RETRY_PRESERVING_SESSION) and the one 503 exception live in one place.
 * - `submit` sends the frozen input EXACTLY: the same object, never rebuilt,
 *   so a Check after a lost answer is byte-identical to the first send.
 * - `fetchPolicy: "no-cache"` on all three: no code, proof id or management
 *   URL is ever written to Apollo's cache.
 * - `managementUrl` is handed on raw; `acceptManagementUrl` in the machine is
 *   the only thing that may call it valid.
 *
 * Nothing here logs. The code and the URL must never reach a console, a
 * report or an error message, and a caught error is discarded, not printed.
 */

type RequestData = { requestGuestEmailCode?: unknown };
type VerifyData = { verifyGuestEmailCode?: unknown };
type BookingData = { createBooking?: (BookingOutcome & { managementUrl?: unknown }) | null };

export function createGuestCheckoutPort(client: ApolloClient): GuestCheckoutPort {
  return {
    async requestCode(input): Promise<Call<CodeRequestAnswer>> {
      try {
        const result = await client.mutate<RequestData>({
          mutation: REQUEST_GUEST_EMAIL_CODE,
          variables: { input: { email: input.email, idempotencyKey: input.idempotencyKey } },
          fetchPolicy: "no-cache",
        });
        const answer = parseCodeRequestAnswer(result.data?.requestGuestEmailCode ?? null);
        return answer ? { kind: "answered", answer } : { kind: "malformed" };
      } catch {
        return { kind: "lost" };
      }
    },

    async verifyCode(input): Promise<Call<CodeVerifyAnswer>> {
      try {
        const result = await client.mutate<VerifyData>({
          mutation: VERIFY_GUEST_EMAIL_CODE,
          variables: {
            input: {
              verificationId: input.verificationId,
              idempotencyKey: input.idempotencyKey,
              oneTimeCode: input.oneTimeCode,
            },
          },
          fetchPolicy: "no-cache",
        });
        const answer = parseCodeVerifyAnswer(result.data?.verifyGuestEmailCode ?? null);
        return answer ? { kind: "answered", answer } : { kind: "malformed" };
      } catch {
        return { kind: "lost" };
      }
    },

    async submit(input: FrozenGuestBookingInput): Promise<SubmitResult> {
      try {
        const result = await client.mutate<BookingData>({
          mutation: CREATE_GUEST_BOOKING,
          // The frozen object itself. Not spread, not rebuilt.
          variables: { input },
          fetchPolicy: "no-cache",
        });
        const payload = result.data?.createBooking;
        // An answer with no payload is not an answer: the booking may exist.
        if (!payload || !payload.appointment) return { kind: "lost" };
        const { managementUrl, ...outcome } = payload;
        return { kind: "answered", outcome: outcome as BookingOutcome, managementUrl: managementUrl ?? null };
      } catch (error) {
        const failure = guestBookingFailureFrom(error);
        return failure.kind === "lost" ? { kind: "lost" } : { kind: "refused", failure };
      }
    },
  };
}
